// fetch() on a bad connection does not fail fast: a request can hang for
// minutes, and a 500 still resolves. Both used to slip through as "no data" -
// the bounding boxes in particular simply never appeared. This gives every
// attempt a deadline, treats a non-2xx answer as a failure, and retries with a
// growing pause before giving up.

export const DEFAULT_TIMEOUT = 15000;
export const DEFAULT_RETRIES = 3;
export const DEFAULT_BACKOFF = 1000;

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

async function attempt(url, options, timeout) {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), timeout);
	try {
		const res = await fetch(url, { ...options, signal: controller.signal });
		if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
		return await res.json();
	} finally {
		clearTimeout(timer);
	}
}

// onRetry(attemptNumber, error) fires before each retry, so the caller can tell
// the customer the connection is slow while it is still trying.
export default async function fetchJson(url, {
	timeout = DEFAULT_TIMEOUT,
	retries = DEFAULT_RETRIES,
	backoff = DEFAULT_BACKOFF,
	onRetry,
	...options
} = {}) {
	let lastError;
	for (let i = 0; i <= retries; i++) {
		try {
			return await attempt(url, options, timeout);
		} catch (err) {
			lastError = err;
			if (i === retries) break;
			if (onRetry) onRetry(i + 1, err);
			await wait(backoff * 2 ** i);
		}
	}
	throw lastError;
}
